import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fa, fdateParts, userHasGroupAccess } from "./data";
import { api, ApiError } from "./api";

const PAGE_SIZE = 10;
const EMPTY_FILTERS = { groupId: "all", personQuery: "", type: "all" };

export default function RecentFeedbacksPage({ state, currentUser }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const accessibleGroups = state.groups.filter((g) => userHasGroupAccess(currentUser, g.id));
  const filtersActive = filters.groupId !== "all" || filters.personQuery || filters.type !== "all";

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.getRecentFeedbacks({
          group_id: filters.groupId,
          type: filters.type,
          q: filters.personQuery.trim(),
          page,
          per_page: PAGE_SIZE,
        });
        if (cancelled) return;
        const pagination = res.data?.pagination || {};
        setItems(res.data?.feedbacks || []);
        setTotal(Number(pagination.total) || 0);
        setPageCount(Math.max(1, Number(pagination.page_count) || 1));
      } catch (err) {
        if (cancelled) return;
        setItems([]);
        setTotal(0);
        setError(err instanceof ApiError ? err.message : "خطا در دریافت بازخوردهای اخیر");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, filters.personQuery ? 250 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [filters, page]);

  function updateFilters(next) {
    setFilters(next);
    setPage(1);
  }

  return (
    <main className="app-shell">
      <section className="hero">
        <div className="hero-line">
          <div>
            <h1>بازخوردهای اخیر</h1>
            <p>آخرین بازخوردهای ثبت‌شده، به ترتیب زمان، همراه با نام کارشناس و گروه او.</p>
          </div>
          <div className="hero-note">{fa(total)} بازخورد</div>
        </div>
      </section>

      <section className="toolbar filters-bar" aria-label="فیلترها">
        <div className="filters-grid">
          <div className="filter-field">
            <label htmlFor="recentFilterGroup">فیلتر گروه</label>
            <select id="recentFilterGroup" className="input select" value={filters.groupId} onChange={(e) => updateFilters({ ...filters, groupId: e.target.value })}>
              <option value="all">همه گروه‌ها</option>
              {accessibleGroups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div className="filter-field">
            <label htmlFor="recentFilterPerson">فیلتر شخص</label>
            <input id="recentFilterPerson" className="input" placeholder="نام کارشناس…" value={filters.personQuery} onChange={(e) => updateFilters({ ...filters, personQuery: e.target.value })} />
          </div>
          <div className="filter-field">
            <label htmlFor="recentFilterType">نوع بازخورد</label>
            <select id="recentFilterType" className="input select" value={filters.type} onChange={(e) => updateFilters({ ...filters, type: e.target.value })}>
              <option value="all">مثبت و منفی</option>
              <option value="negative">فقط منفی</option>
              <option value="positive">فقط مثبت</option>
            </select>
          </div>
        </div>
        <div className="filters-actions">
          <button className="btn" type="button" disabled={!filtersActive} onClick={() => updateFilters(EMPTY_FILTERS)}>حذف فیلترها</button>
        </div>
      </section>

      <section className="groups">
        {loading && <div className="empty">در حال بارگذاری…</div>}
        {!loading && error && <div className="empty">{error}</div>}
        {!loading && !error && !items.length && <div className="empty">بازخوردی برای نمایش وجود ندارد.</div>}
        {!loading && !error && items.map((f) => {
          const isPositive = f.type === "positive";
          const when = fdateParts(f.created_at);
          const personName = f.person?.name || "کارشناس حذف‌شده";
          return (
            <article
              className={"person in-group" + (f.person ? " recent-card" : "")}
              key={f.id}
              onClick={() => { if (f.person) navigate("/?history=" + f.person.id); }}
            >
              <div className="person-top">
                <div className="name-block">
                  <div className="name">{personName}</div>
                  <div className="meta">
                    {f.group ? f.group.name + " · " : ""}
                    {when.date}{when.time ? " · ساعت " + when.time : ""}
                  </div>
                </div>
                <span className={"feedback-type-badge" + (isPositive ? " positive" : " negative")}>
                  {isPositive ? "مثبت" : "منفی"}
                </span>
              </div>
              {f.caption
                ? <div className="recent-caption">{f.caption}</div>
                : <div className="meta" style={{ marginTop: 8 }}>بدون توضیح</div>}
              {f.image && <img className="history-item-image" src={f.image} alt="تصویر بازخورد" />}
            </article>
          );
        })}
      </section>

      {pageCount > 1 && (
        <div className="pagination">
          <button className="btn btn-icon" type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
          <span className="pagination-info">صفحه {fa(page)} از {fa(pageCount)}</span>
          <button className="btn btn-icon" type="button" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>›</button>
        </div>
      )}
    </main>
  );
}
